// src/services/pricingService.js
// pricing_rules: reglas de pricing (tarifas, mínimos, suplementos, etc.)
// Objetivo: encapsular CRUD y helpers de cálculo básico (sin imponer tu lógica final).
//
// Nota: Como no has confirmado el schema exacto de pricing_rules, este servicio es
// deliberadamente "genérico" y no asume campos más allá de team_id + id.
// Cuando lleguemos a la UI de Finance o Pricing, lo afinamos.

import { getSupabaseClient, normalizeSupabaseError } from "./supabaseClient.js";

export const TABLE_PRICING = "pricing_rules";

// =========================
// Helpers internos
// =========================
function assertId(id, name = "id") {
  if (id === null || id === undefined || String(id).trim() === "") {
    throw new Error(`${name} es obligatorio.`);
  }
}

function pickColumns(obj, whitelist) {
  if (!whitelist) return { ...obj };
  const out = {};
  for (const k of whitelist) {
    if (Object.prototype.hasOwnProperty.call(obj, k)) out[k] = obj[k];
  }
  return out;
}

function throwSupabase(error, context = "") {
  if (!error) return;
  const n = normalizeSupabaseError(error);
  const msg = context ? `${context}: ${n.message}` : n.message;
  const e = new Error(msg);
  e.code = n.code;
  e.details = n.details;
  e.hint = n.hint;
  e.raw = n.raw;
  throw e;
}

// =========================
// Lecturas
// =========================

/**
 * Lista reglas de pricing por team.
 */
export async function listPricingRulesByTeam(
  teamId,
  { orderBy = "created_at", ascending = false, select = "*" } = {}
) {
  assertId(teamId, "teamId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase
    .from(TABLE_PRICING)
    .select(select)
    .eq("team_id", teamId)
    .order(orderBy, { ascending });

  throwSupabase(error, "Error listando pricing_rules");
  return data || [];
}

/**
 * Obtiene una regla por id.
 */
export async function getPricingRuleById(ruleId, { select = "*" } = {}) {
  assertId(ruleId, "ruleId");
  const supabase = getSupabaseClient();

  const { data, error } = await supabase.from(TABLE_PRICING).select(select).eq("id", ruleId).single();
  throwSupabase(error, "Error leyendo pricing_rule");
  return data;
}

// =========================
// Escrituras
// =========================

/**
 * Crea regla de pricing.
 */
export async function createPricingRule(payload, { whitelist = null, select = "*" } = {}) {
  if (!payload || typeof payload !== "object") throw new Error("payload inválido.");
  if (!payload.team_id) throw new Error("payload.team_id obligatorio.");

  const supabase = getSupabaseClient();
  const insertPayload = pickColumns(payload, whitelist);

  const { data, error } = await supabase.from(TABLE_PRICING).insert(insertPayload).select(select).single();
  throwSupabase(error, "Error creando pricing_rule");
  return data;
}

/**
 * Actualiza regla de pricing.
 */
export async function updatePricingRule(ruleId, patch, { whitelist = null } = {}) {
  assertId(ruleId, "ruleId");
  if (!patch || typeof patch !== "object") throw new Error("patch inválido.");

  const supabase = getSupabaseClient();
  const updatePayload = pickColumns(patch, whitelist);

  const { error } = await supabase.from(TABLE_PRICING).update(updatePayload).eq("id", ruleId);
  throwSupabase(error, "Error actualizando pricing_rule");
  return true;
}

/**
 * Borra regla de pricing.
 */
export async function deletePricingRule(ruleId) {
  assertId(ruleId, "ruleId");
  const supabase = getSupabaseClient();

  const { error } = await supabase.from(TABLE_PRICING).delete().eq("id", ruleId);
  throwSupabase(error, "Error borrando pricing_rule");
  return true;
}

// =========================
// Precio por km/region
// =========================

/**
 * Busca en pricing_rules (activas, ordenadas por priority desc) la primera
 * regla cuyo region_match (si tiene) coincida con context.region, cuyo
 * km_min/km_max (si tienen) encajen con context.km, y cuyo
 * equipment_included (si tiene) coincida con context.equipmentIncluded.
 * En los tres casos, null en la regla = comodin (aplica igual). Permite
 * reglas genericas y reglas mas especificas con mayor priority para casos
 * especiales.
 *
 * @param {string} teamId
 * @param {{ km: number, region?: string, equipmentIncluded?: boolean }} context
 * @returns {Promise<object|null>} la regla que matchea, o null si ninguna aplica
 */
export async function getPriceForContext(teamId, { km, region, equipmentIncluded } = {}) {
  if (!Number.isFinite(Number(km))) return null;

  const rules = await listPricingRulesByTeam(teamId, { orderBy: "priority", ascending: false });
  const normalizedRegion = region ? String(region).trim().toLowerCase() : null;

  for (const r of rules) {
    if (!r.active) continue;

    if (r.region_match) {
      if (!normalizedRegion || String(r.region_match).trim().toLowerCase() !== normalizedRegion) continue;
    }

    if (r.km_min !== null && r.km_min !== undefined && km < r.km_min) continue;
    if (r.km_max !== null && r.km_max !== undefined && km > r.km_max) continue;

    if (r.equipment_included !== null && r.equipment_included !== undefined) {
      if (Boolean(r.equipment_included) !== Boolean(equipmentIncluded)) continue;
    }

    return r;
  }

  return null;
}

// =========================
// Helpers (cálculo base, opcional)
// =========================

/**
 * Aplica reglas a un "contexto" y devuelve un precio sugerido.
 * Como no tenemos schema confirmado, este helper es un placeholder controlado:
 *
 * - Si una regla tiene campo `fixed_price`, lo usa como base.
 * - Si tiene `min_price`, aplica mínimo.
 * - Si tiene `percentage`, aplica % sobre base.
 *
 * context esperado (ejemplos):
 * { basePrice, type, region, channel, date, attendees, distanceKm }
 *
 * Nota: este helper NO se usa hasta que tú lo pidas en UI.
 */
export function computeSuggestedPrice(rules = [], context = {}) {
  const base = Number(context.basePrice ?? 0);
  let price = Number.isFinite(base) ? base : 0;

  for (const r of rules || []) {
    if (r == null || typeof r !== "object") continue;

    // fixed_price
    if (r.fixed_price !== undefined && r.fixed_price !== null) {
      const fp = Number(r.fixed_price);
      if (Number.isFinite(fp)) price = fp;
    }

    // percentage
    if (r.percentage !== undefined && r.percentage !== null) {
      const p = Number(r.percentage);
      if (Number.isFinite(p)) price = price * (1 + p / 100);
    }

    // min_price
    if (r.min_price !== undefined && r.min_price !== null) {
      const mp = Number(r.min_price);
      if (Number.isFinite(mp)) price = Math.max(price, mp);
    }
  }

  // redondeo suave a 2 decimales
  return Math.round((price + Number.EPSILON) * 100) / 100;
}
